import { Component, OnInit, OnDestroy, ViewChild, ElementRef, Output, EventEmitter } from '@angular/core';
import { PatientConsentFacade, PatientConsentUiState } from './patient-consent.facade';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable, Subscription } from 'rxjs';
import { COMMON_IMPORTS } from '../../global-misc/common-imports';
import { RadioButtonModule } from 'primeng/radiobutton';
import { DialogModule } from 'primeng/dialog';

@Component({
  selector: 'app-patient-consent',
  imports: [...COMMON_IMPORTS, RadioButtonModule, DialogModule],
  templateUrl: './patient-consent.component.html',
  styleUrls: ['./patient-consent.component.css'],
})
export class PatientConsentComponent implements OnInit, OnDestroy {
  @ViewChild('qrCodeContainer', { static: false }) qrCodeContainer!: ElementRef;
  @Output() onConsentSaved = new EventEmitter<string>();

  public uiState$: Observable<PatientConsentUiState>;
  public form: FormGroup;
  public scriptExpanded: boolean = false;
  public qrModalVisible: boolean = false;

  private subscription: Subscription = new Subscription();
  private qrCodeGenerated: boolean = false;

  constructor(
    private facade: PatientConsentFacade,
    private formBuilder: FormBuilder,
  ) {
    this.uiState$ = this.facade.uiState$;
    // Initialize form immediately to avoid timing issues
    this.form = this.formBuilder.group({
      consentStatus: ['', Validators.required],
    });
  }

  ngOnInit(): void {
    this.facade.initializeComponent();

    this.subscription.add(
      this.uiState$.subscribe((uiState: PatientConsentUiState) => {
        // Keep the existing form but sync its value with the facade's form if needed
        if (uiState.form) {
          const consentValue = uiState.form.get('consentStatus')?.value;
          if (consentValue && consentValue !== this.form.get('consentStatus')?.value) {
            this.form.get('consentStatus')?.setValue(consentValue);
          }
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  public onClose(): void {
    this.facade.onClose();
  }

  public toggleScript(): void {
    this.scriptExpanded = !this.scriptExpanded;
  }

  public showQrModal(): void {
    this.qrModalVisible = true;
    this.generateQrCode();
  }

  public async onSaveConsent(): Promise<void> {
    // Update the facade's form with our current values before saving
    const consentValue = this.form.get('consentStatus')?.value;
    this.facade.updateConsentValue(consentValue);

    await this.facade.saveConsent();
    if (consentValue && (consentValue === 'given' || consentValue === 'not-given')) {
      this.onConsentSaved.emit(consentValue);
    }
  }

  public get isFormValid(): boolean {
    const consentValue = this.form?.get('consentStatus')?.value;
    return this.form?.valid && consentValue && (consentValue === 'given' || consentValue === 'not-given');
  }

  public get consentStatusControl() {
    return this.form?.get('consentStatus');
  }

  private generateQrCode(): void {
    if (!this.qrCodeGenerated && this.qrCodeContainer) {
      // Using a simple QR code generation approach
      // You can replace this with a proper QR code library like qrcode or angularx-qrcode
      setTimeout(() => {
        if (this.qrCodeContainer?.nativeElement) {
          const qrUrl = 'https://www.ulster.ac.uk/research/topic/biomedical-sciences/optometry-and-vision-science';
          const qrCodeImg = document.createElement('img');
          qrCodeImg.src = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrUrl)}`;
          qrCodeImg.alt = 'QR Code';
          qrCodeImg.style.width = '200px';
          qrCodeImg.style.height = '200px';
          this.qrCodeContainer.nativeElement.appendChild(qrCodeImg);
          this.qrCodeGenerated = true;
        }
      }, 100);
    }
  }
}
